import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, deleteDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Icons } from '../components/Icons';
import { useAuth } from '../hooks/useAuth';
import { sendSlackNotification } from '../utils/slackNotification';
import { sendLocationChangeNotification } from '../utils/sendNotification';

function InventoryListEnhanced() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // grid, list, table
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, priceHigh, priceLow, name
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, item: null });
  const [deleting, setDeleting] = useState(false);
  const [locationEdit, setLocationEdit] = useState({ show: false, item: null });

  // URL에서 필터 상태 복원
  const getInitialFilters = () => {
    return {
      search: searchParams.get('search') || '',
      categories: searchParams.get('categories') ? searchParams.get('categories').split(',') : [],
      locations: searchParams.get('locations') ? searchParams.get('locations').split(',') : [],
      statuses: searchParams.get('statuses') ? searchParams.get('statuses').split(',') : [],
      ownershipTypes: searchParams.get('ownershipTypes') ? searchParams.get('ownershipTypes').split(',') : [],
      designers: searchParams.get('designers') ? searchParams.get('designers').split(',') : [],
      manufacturers: searchParams.get('manufacturers') ? searchParams.get('manufacturers').split(',') : [],
      materials: searchParams.get('materials') ? searchParams.get('materials').split(',') : [],
      condition: searchParams.get('condition') || '',
      priceMin: searchParams.get('priceMin') || '',
      priceMax: searchParams.get('priceMax') || '',
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || ''
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);

  // 필터 변경 시 URL 업데이트
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) params.set('search', filters.search);
    if (filters.categories.length > 0) params.set('categories', filters.categories.join(','));
    if (filters.locations.length > 0) params.set('locations', filters.locations.join(','));
    if (filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','));
    if (filters.ownershipTypes.length > 0) params.set('ownershipTypes', filters.ownershipTypes.join(','));
    if (filters.designers.length > 0) params.set('designers', filters.designers.join(','));
    if (filters.manufacturers.length > 0) params.set('manufacturers', filters.manufacturers.join(','));
    if (filters.materials.length > 0) params.set('materials', filters.materials.join(','));
    if (filters.condition) params.set('condition', filters.condition);
    if (filters.priceMin) params.set('priceMin', filters.priceMin);
    if (filters.priceMax) params.set('priceMax', filters.priceMax);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // 동적 필터 옵션 (Firebase에서 로드)
  const [allLocations, setAllLocations] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  const [allOwnershipTypes] = useState(['직판', '위탁', '신제품']); // 소유구분 옵션
  const [_allConditions, setAllConditions] = useState([]); // eslint-disable-line no-unused-vars
  const [allZones, setAllZones] = useState([]);
  const [selectedZoneForMove, setSelectedZoneForMove] = useState({ id: '', name: '' });
  const [allDesigners, setAllDesigners] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);

  // 필터 내 검색 상태
  const [designerSearch, setDesignerSearch] = useState('');
  const [manufacturerSearch, setManufacturerSearch] = useState('');
  const [materialSearch, setMaterialSearch] = useState('');

  const loadInventory = async () => {
    try {
      const inventoryRef = collection(db, 'inventory');
      const q = query(inventoryRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setInventory(items);
    } catch (error) {
      console.error('재고 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      // 위치 로드 (order 순으로 정렬)
      const locationsRef = collection(db, 'locations');
      const locationsSnapshot = await getDocs(locationsRef);
      const locationsList = locationsSnapshot.docs
        .map(doc => ({ name: doc.data().name, order: doc.data().order ?? 999 }))
        .sort((a, b) => a.order - b.order)
        .map(loc => loc.name);
      setAllLocations(locationsList);

      // Zone 로드
      const zonesRef = collection(db, 'zones');
      const zonesSnapshot = await getDocs(zonesRef);
      const zonesList = zonesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          name: doc.data().name,
          locationId: doc.data().locationId,
          locationName: doc.data().locationName,
          order: doc.data().order ?? 999,
          active: doc.data().active !== false,
          isDisplay: doc.data().isDisplay
        }))
        .filter(zone => zone.active && zone.isDisplay)
        .sort((a, b) => a.order - b.order);
      setAllZones(zonesList);

      // 카테고리(대) 로드
      const categoriesRef = collection(db, 'filter_categories_large');
      const categoriesSnapshot = await getDocs(categoriesRef);
      const categoriesList = categoriesSnapshot.docs.map(doc => doc.data().name);
      setAllCategories(categoriesList);

      // 상태(대) 로드
      const statusesRef = collection(db, 'filter_statuses_large');
      const statusesSnapshot = await getDocs(statusesRef);
      const statusesList = statusesSnapshot.docs.map(doc => doc.data().name);
      setAllStatuses(statusesList);

      // 컨디션 로드
      const conditionsRef = collection(db, 'filter_conditions');
      const conditionsSnapshot = await getDocs(conditionsRef);
      const conditionsList = conditionsSnapshot.docs.map(doc => doc.data().name);
      setAllConditions(conditionsList);

      // 디자이너, 제조사, 소재 로드 (재고 데이터에서 추출)
      const inventoryRef = collection(db, 'inventory');
      const inventorySnapshot = await getDocs(inventoryRef);

      const designerSet = new Set();
      const manufacturerSet = new Set();
      const materialSet = new Set();

      inventorySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.designer && data.designer.trim()) {
          designerSet.add(data.designer.trim());
        }
        if (data.manufacturer && data.manufacturer.trim()) {
          manufacturerSet.add(data.manufacturer.trim());
        }
        if (data.material && data.material.trim()) {
          materialSet.add(data.material.trim());
        }
      });

      setAllDesigners(Array.from(designerSet).sort());
      setAllManufacturers(Array.from(manufacturerSet).sort());
      setAllMaterials(Array.from(materialSet).sort());
    } catch (error) {
      console.error('필터 옵션 로드 오류:', error);
      // 오류 발생 시 빈 배열로 설정
      setAllCategories([]);
      setAllStatuses([]);
      setAllConditions([]);
      setAllDesigners([]);
      setAllManufacturers([]);
      setAllMaterials([]);
    }
  };

  useEffect(() => {
    loadInventory();
    loadFilterOptions();
  }, []);

  const filteredInventory = React.useMemo(() => {
    let filtered = [...inventory];

    // 검색 필터
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.productName?.toLowerCase().includes(searchLower) ||
        item.barcodeId?.toLowerCase().includes(searchLower) ||
        item.categoryLarge?.toLowerCase().includes(searchLower) ||
        item.categoryMedium?.toLowerCase().includes(searchLower) ||
        item.designer?.toLowerCase().includes(searchLower) ||
        item.material?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      );
    }

    // 카테고리 필터 (다중 선택)
    if (filters.categories.length > 0) {
      filtered = filtered.filter(item => filters.categories.includes(item.categoryLarge));
    }

    // 위치 필터 (다중 선택)
    if (filters.locations.length > 0) {
      filtered = filtered.filter(item => filters.locations.includes(item.location));
    }

    // 상태 필터 (다중 선택)
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(item => filters.statuses.includes(item.statusLarge));
    }

    // 소유구분 필터 (다중 선택)
    if (filters.ownershipTypes.length > 0) {
      filtered = filtered.filter(item => filters.ownershipTypes.includes(item.ownershipType || item.category || '직판'));
    }

    // 디자이너 필터 (다중 선택)
    if (filters.designers.length > 0) {
      filtered = filtered.filter(item => filters.designers.includes(item.designer));
    }

    // 제조사 필터 (다중 선택)
    if (filters.manufacturers.length > 0) {
      filtered = filtered.filter(item => filters.manufacturers.includes(item.manufacturer));
    }

    // 소재 필터 (다중 선택)
    if (filters.materials.length > 0) {
      filtered = filtered.filter(item => filters.materials.includes(item.material));
    }

    // 컨디션 필터
    if (filters.condition) {
      filtered = filtered.filter(item => item.condition === filters.condition);
    }

    // 가격 범위 필터
    if (filters.priceMin) {
      filtered = filtered.filter(item => item.salePrice >= parseInt(filters.priceMin));
    }
    if (filters.priceMax) {
      filtered = filtered.filter(item => item.salePrice <= parseInt(filters.priceMax));
    }

    // 날짜 범위 필터
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(item => item.createdAt >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59);
      filtered = filtered.filter(item => item.createdAt <= toDate);
    }

    // 정렬 적용 (판매완료 상태는 항상 맨 아래로)
    filtered.sort((a, b) => {
      // 판매완료 상태는 맨 아래로
      const aIsSold = a.status === '판매완료';
      const bIsSold = b.status === '판매완료';
      if (aIsSold !== bIsSold) {
        return aIsSold ? 1 : -1;
      }

      // 기존 정렬 로직
      switch (sortBy) {
        case 'newest':
          return b.createdAt - a.createdAt;
        case 'oldest':
          return a.createdAt - b.createdAt;
        case 'priceHigh':
          return (b.salePrice || 0) - (a.salePrice || 0);
        case 'priceLow':
          return (a.salePrice || 0) - (b.salePrice || 0);
        case 'name':
          return (a.productName || '').localeCompare(b.productName || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [inventory, filters, sortBy]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleMultiSelect = (key, value) => {
    setFilters(prev => {
      const current = prev[key];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [key]: updated };
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      categories: [],
      locations: [],
      statuses: [],
      ownershipTypes: [],
      condition: '',
      priceMin: '',
      priceMax: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleDeleteClick = (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModal({ show: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;

    setDeleting(true);
    try {
      const item = deleteModal.item;
      await deleteDoc(doc(db, 'inventory', item.id));

      // Slack 알림 발송
      await sendSlackNotification('inventoryDelete', {
        productName: item.productName,
        barcodeId: item.barcodeId,
        location: item.location,
        category: item.categoryLarge || item.category
      }, user?.email || 'unknown');

      // 목록에서 제거
      setInventory(prev => prev.filter(inv => inv.id !== item.id));
      setDeleteModal({ show: false, item: null });
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const handleLocationClick = (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocationEdit({ show: true, item });
  };

  // SKU 위치 집계 함수
  const getSkuLocationSummary = (item) => {
    if (!item.skuLocations || item.skuLocations.length === 0) {
      return item.location;
    }

    // 실제 SKU 개수를 기준으로 판단
    const actualSkuCount = item.skuLocations.length;
    const isBulkProduct = actualSkuCount > 1;

    // SKU 위치별로 그룹화
    const grouped = {};
    item.skuLocations.forEach(sku => {
      const loc = sku.location || item.location;
      if (!grouped[loc]) {
        grouped[loc] = [];
      }
      grouped[loc].push(sku);
    });

    // "위치 (개수)" 형식으로 반환
    const entries = Object.entries(grouped);
    if (entries.length === 0) return item.location;

    // 수량 관리 제품은 항상 개수 표시
    if (isBulkProduct) {
      return entries.map(([loc, items]) => `${loc} (${items.length}개)`).join(', ');
    }

    // 일품 제품은 개수 표시 안함
    if (entries.length === 1) {
      return entries[0][0];
    }

    return entries.map(([loc, items]) => `${loc} (${items.length}개)`).join(', ');
  };

  const handleLocationChange = async (newLocation, moveAll = false) => {
    if (!locationEdit.item) return;

    try {
      const item = locationEdit.item;
      const docRef = doc(db, 'inventory', item.id);
      const quantity = Number(item.quantity) || 1;
      const isBulkProduct = quantity > 1 || item.productType === 'bulk';

      // Zone 정보 포함해서 업데이트
      const updateData = {
        location: newLocation,
        updatedAt: serverTimestamp()
      };

      // Zone이 선택된 경우 Zone 정보도 업데이트
      if (selectedZoneForMove.id) {
        updateData.zoneId = selectedZoneForMove.id;
        updateData.zoneName = `${selectedZoneForMove.locationName} - ${selectedZoneForMove.name}`;
      }

      // SKU 위치 동기화
      if (isBulkProduct) {
        // 수량 관리 제품인 경우
        if (moveAll) {
          // 전체 이동: 모든 SKU 위치 업데이트
          let skuLocations = item.skuLocations;

          // skuLocations가 없으면 초기화
          if (!skuLocations || skuLocations.length === 0) {
            skuLocations = Array.from({ length: quantity }, (_, i) => ({
              sku: String(i + 1).padStart(2, '0'),
              location: newLocation,
              zoneId: selectedZoneForMove.id || '',
              zoneName: selectedZoneForMove.name || ''
            }));
          } else {
            // 모든 SKU의 위치를 새 위치로 업데이트
            skuLocations = skuLocations.map(sku => ({
              ...sku,
              location: newLocation,
              zoneId: selectedZoneForMove.id || sku.zoneId || '',
              zoneName: selectedZoneForMove.name || sku.zoneName || ''
            }));
          }

          updateData.skuLocations = skuLocations;
        }
        // moveAll이 false면 skuLocations는 업데이트하지 않음 (부분 이동)
      } else {
        // 재고 1개 제품: SKU 위치 동기화
        let skuLocations = item.skuLocations;

        if (!skuLocations || skuLocations.length === 0) {
          // skuLocations가 없으면 생성
          skuLocations = [{
            sku: '01',
            location: newLocation,
            zoneId: selectedZoneForMove.id || '',
            zoneName: selectedZoneForMove.name || ''
          }];
        } else {
          // 기존 skuLocations 업데이트
          skuLocations = skuLocations.map(sku => ({
            ...sku,
            location: newLocation,
            zoneId: selectedZoneForMove.id || sku.zoneId || '',
            zoneName: selectedZoneForMove.name || sku.zoneName || ''
          }));
        }

        updateData.skuLocations = skuLocations;
      }

      await updateDoc(docRef, updateData);

      // 이력 기록
      const changeType = moveAll ? '전체위치변경' : '위치변경';
      await addDoc(collection(db, 'inventory_history'), {
        productId: item.id,
        changeType,
        previousValue: item.location,
        newValue: newLocation + (selectedZoneForMove.name ? ` (${selectedZoneForMove.name})` : ''),
        ...(moveAll && { skuCount: quantity }),
        timestamp: serverTimestamp(),
        userId: user?.email || 'unknown'
      });

      // Slack 알림 발송
      await sendSlackNotification('inventoryMove', {
        productName: item.productName,
        barcodeId: item.barcodeId,
        fromLocation: item.location,
        toLocation: newLocation + (selectedZoneForMove.name ? ` (${selectedZoneForMove.name})` : ''),
        ...(moveAll && { skuCount: quantity, moveType: '전체이동' })
      }, user?.email || 'unknown');

      // 앱 내 알림 발송
      await sendLocationChangeNotification(item.id, item.productName, item.location, newLocation, user?.email || 'unknown');

      // 목록에서 업데이트
      setInventory(prev => prev.map(inv =>
        inv.id === item.id ? {
          ...inv,
          location: newLocation,
          zoneId: selectedZoneForMove.id || inv.zoneId,
          zoneName: selectedZoneForMove.id ? `${selectedZoneForMove.locationName} - ${selectedZoneForMove.name}` : inv.zoneName,
          ...(updateData.skuLocations && { skuLocations: updateData.skuLocations })
        } : inv
      ));
      setLocationEdit({ show: false, item: null });
      setSelectedZoneForMove({ id: '', name: '' });

      // 성공 메시지
      if (moveAll) {
        alert(`전체 ${quantity}개의 SKU가 ${newLocation}(으)로 이동되었습니다.`);
      }
    } catch (error) {
      console.error('위치 변경 오류:', error);
      alert('위치 변경 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">재고 목록</h2>
          <p className="text-brand-500 mt-1">총 {filteredInventory.length}개 항목</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/scan"
            className="inline-flex items-center justify-center w-11 h-11 bg-white hover:bg-gray-100 border border-gray-300 text-gray-600 rounded-lg transition-colors shadow-sm"
            title="바코드 스캐너"
          >
            <Icons.Scan className="w-6 h-6" />
          </Link>
          <Link
            to="/add"
            className="inline-flex items-center h-11 px-5 bg-brand-900 hover:bg-brand-800 text-white rounded-lg transition-colors shadow-sm font-medium"
          >
            <Icons.Add className="w-5 h-5 mr-2" />
            재고 등록
          </Link>
        </div>
      </div>

      {/* 기본 필터 & 뷰 전환 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="제품명, 바코드, 설명 검색..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            />
          </div>

          <div className="md:col-span-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
            >
              <option value="newest">최신순</option>
              <option value="oldest">오래된순</option>
              <option value="priceHigh">가격 높은순</option>
              <option value="priceLow">가격 낮은순</option>
              <option value="name">이름순</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`w-full px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center font-medium ${showAdvancedFilters
                ? 'bg-brand-100 text-brand-800 border border-brand-200'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              필터
            </button>
          </div>

          <div className="md:col-span-2 flex bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex-1 flex items-center justify-center rounded-md transition-all ${viewMode === 'grid'
                ? 'bg-white text-brand-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              title="그리드 뷰"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center rounded-md transition-all ${viewMode === 'list'
                ? 'bg-white text-brand-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              title="리스트 뷰"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex-1 flex items-center justify-center rounded-md transition-all ${viewMode === 'table'
                ? 'bg-white text-brand-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
              title="테이블 뷰"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7-4h14M5 21l7-14 7 14M5 19h14" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 고급 필터 */}
      {showAdvancedFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 mb-4 animate-fade-in-down">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-brand-900">상세 필터</h3>
            <button
              onClick={resetFilters}
              className="text-sm text-red-500 hover:text-red-700 font-medium hover:underline"
            >
              필터 초기화
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* 카테고리 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">카테고리</label>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {allCategories.map(cat => (
                  <label key={cat} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.categories.includes(cat)}
                      onChange={() => toggleMultiSelect('categories', cat)}
                      className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{cat}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 상태 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
              <div className="space-y-2">
                {allStatuses.map(status => (
                  <label key={status} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes(status)}
                      onChange={() => toggleMultiSelect('statuses', status)}
                      className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 소유구분 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">소유구분</label>
              <div className="space-y-2">
                {allOwnershipTypes.map(type => (
                  <label key={type} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.ownershipTypes.includes(type)}
                      onChange={() => toggleMultiSelect('ownershipTypes', type)}
                      className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 위치 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">위치</label>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {allLocations.map(loc => (
                  <label key={loc} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.locations.includes(loc)}
                      onChange={() => toggleMultiSelect('locations', loc)}
                      className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                    />
                    <span className="text-sm text-gray-700">{loc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 디자이너 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Designer</label>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="검색..."
                  value={designerSearch}
                  onChange={(e) => setDesignerSearch(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {allDesigners
                  .filter(designer => designer.toLowerCase().includes(designerSearch.toLowerCase()))
                  .map(designer => (
                    <label key={designer} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.designers.includes(designer)}
                        onChange={() => toggleMultiSelect('designers', designer)}
                        className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{designer}</span>
                    </label>
                  ))}
              </div>
            </div>

            {/* 제조사 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Manufacturer</label>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="검색..."
                  value={manufacturerSearch}
                  onChange={(e) => setManufacturerSearch(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {allManufacturers
                  .filter(manufacturer => manufacturer.toLowerCase().includes(manufacturerSearch.toLowerCase()))
                  .map(manufacturer => (
                    <label key={manufacturer} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.manufacturers.includes(manufacturer)}
                        onChange={() => toggleMultiSelect('manufacturers', manufacturer)}
                        className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{manufacturer}</span>
                    </label>
                  ))}
              </div>
            </div>

            {/* 소재 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Material</label>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="검색..."
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {allMaterials
                  .filter(material => material.toLowerCase().includes(materialSearch.toLowerCase()))
                  .map(material => (
                    <label key={material} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.materials.includes(material)}
                        onChange={() => toggleMultiSelect('materials', material)}
                        className="rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{material}</span>
                    </label>
                  ))}
              </div>
            </div>

            {/* 가격 및 날짜 */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">가격 범위</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    placeholder="최소"
                    value={filters.priceMin}
                    onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="최대"
                    value={filters.priceMax}
                    onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">등록일</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 뷰 렌더링 */}
      {viewMode === 'grid' && <GridView items={filteredInventory} onDelete={handleDeleteClick} onLocationClick={handleLocationClick} getLocationDisplay={getSkuLocationSummary} />}
      {viewMode === 'list' && <ListView items={filteredInventory} onDelete={handleDeleteClick} onLocationClick={handleLocationClick} getLocationDisplay={getSkuLocationSummary} />}
      {viewMode === 'table' && <TableView items={filteredInventory} onDelete={handleDeleteClick} onLocationClick={handleLocationClick} getLocationDisplay={getSkuLocationSummary} />}

      {/* 삭제 확인 모달 */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 animate-fade-in-down">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <Icons.Delete className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">재고 삭제</h3>
            <p className="text-gray-600 text-center mb-2">
              <span className="font-semibold text-gray-900">{deleteModal.item?.productName}</span>
            </p>
            <p className="text-sm text-gray-500 text-center mb-6">
              이 항목을 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, item: null })}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    삭제중...
                  </>
                ) : (
                  '삭제'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 위치 수정 모달 */}
      {locationEdit.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 animate-fade-in-down">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-brand-100 rounded-full">
              <Icons.MapPin className="w-6 h-6 text-brand-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">위치 변경</h3>
            <p className="text-gray-600 text-center mb-2">
              <span className="font-semibold text-gray-900">{locationEdit.item?.productName}</span>
            </p>
            <p className="text-sm text-gray-500 text-center mb-4">
              현재 위치: <span className="font-medium text-brand-600">{locationEdit.item && getSkuLocationSummary(locationEdit.item)}</span>
            </p>

            {/* 다량 제품 이동 옵션 안내 */}
            {(Number(locationEdit.item?.quantity) > 1 || locationEdit.item?.productType === 'bulk') && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  <span className="font-semibold">수량: {locationEdit.item?.quantity}개</span>
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-blue-700">
                    <span className="font-medium">📦 전체 이동:</span> 아래 위치 버튼 클릭 시 모든 SKU가 이동됩니다.
                  </div>
                  <div className="text-xs text-blue-700">
                    <span className="font-medium">📋 일부 이동:</span> 아래 버튼으로 상세 페이지에서 선택하세요.
                  </div>
                </div>
                <button
                  onClick={() => {
                    setLocationEdit({ show: false, item: null });
                    navigate(`/inventory/${locationEdit.item?.id}`);
                  }}
                  className="w-full mt-2 text-xs px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded transition font-medium"
                >
                  일부 이동하러 가기 →
                </button>
              </div>
            )}

            {/* Zone 선택 (선택사항) */}
            {allZones.length > 0 && (
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">Zone 배정 (선택사항)</label>
                <select
                  value={selectedZoneForMove.id}
                  onChange={(e) => {
                    const zone = allZones.find(z => z.id === e.target.value);
                    setSelectedZoneForMove(zone ? { id: zone.id, name: zone.name, locationName: zone.locationName } : { id: '', name: '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Zone 선택 안함</option>
                  {allZones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.locationName} - {zone.name}
                    </option>
                  ))}
                </select>
                {/* Zone만 적용 버튼 */}
                {selectedZoneForMove.id && (
                  <button
                    onClick={() => {
                      const isBulk = Number(locationEdit.item?.quantity) > 1 || locationEdit.item?.productType === 'bulk';
                      handleLocationChange(locationEdit.item?.location, isBulk);
                    }}
                    className="w-full mt-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    Zone만 적용
                  </button>
                )}
              </div>
            )}

            <p className="text-xs text-gray-400 text-center mb-2">
              {(Number(locationEdit.item?.quantity) > 1 || locationEdit.item?.productType === 'bulk') ? '전체 이동' : '위치 변경'}
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {allLocations.map(location => (
                <button
                  key={location}
                  onClick={() => {
                    const isBulk = Number(locationEdit.item?.quantity) > 1 || locationEdit.item?.productType === 'bulk';
                    handleLocationChange(location, isBulk);
                  }}
                  disabled={location === locationEdit.item?.location}
                  className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${
                    location === locationEdit.item?.location
                      ? 'bg-brand-100 text-brand-700 font-medium cursor-not-allowed'
                      : 'bg-gray-50 hover:bg-brand-50 text-gray-700 hover:text-brand-700'
                  }`}
                >
                  {location}
                  {location === locationEdit.item?.location && (
                    <span className="float-right text-brand-500">현재 위치</span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setLocationEdit({ show: false, item: null });
                setSelectedZoneForMove({ id: '', name: '' });
              }}
              className="w-full mt-4 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {filteredInventory.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-12 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">검색 결과가 없습니다</h3>
          <p className="text-gray-500">다른 검색어나 필터를 시도해보세요.</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-4 py-2 bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200 transition-colors text-sm font-medium"
          >
            필터 초기화
          </button>
        </div>
      )}
    </div>
  );
}

// 그리드 뷰 컴포넌트
function GridView({ items, onDelete, onLocationClick, getLocationDisplay }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {items.map(item => (
        <InventoryCard key={item.id} item={item} onDelete={onDelete} onLocationClick={onLocationClick} getLocationDisplay={getLocationDisplay} />
      ))}
    </div>
  );
}

// 리스트 뷰 컴포넌트
function ListView({ items, onDelete, onLocationClick, getLocationDisplay }) {
  const statusColors = {
    '판매가능': 'bg-green-100 text-green-800',
    '수리중': 'bg-yellow-100 text-yellow-800',
    '청소중': 'bg-blue-100 text-blue-800',
    '예약': 'bg-purple-100 text-purple-800',
    '판매완료': 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="space-y-2 md:space-y-4">
      {items.map(item => (
        <div key={item.id} className="bg-white rounded-xl shadow-sm border border-brand-100 hover:shadow-md transition group relative">
          {/* 모바일 간소화 뷰 */}
          <div className="md:hidden flex items-center justify-between p-3">
            <Link to={`/inventory/${item.id}`} className="font-medium text-gray-900 truncate flex-1 mr-3">
              {item.productName}
            </Link>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex flex-col items-end">
                <button
                  onClick={(e) => onLocationClick(item, e)}
                  className="text-sm text-brand-600 hover:text-brand-800 hover:bg-brand-50 px-2 py-1 rounded transition-colors flex items-center"
                  title="위치 변경"
                >
                  {getLocationDisplay(item)}
                  <Icons.Edit className="w-3 h-3 ml-1 opacity-50" />
                </button>
                {item.zoneName && (
                  <span className="text-xs text-purple-600">📍 {item.zoneName.split(' - ').pop()}</span>
                )}
              </div>
              <button
                onClick={(e) => onDelete(item, e)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="삭제"
              >
                <Icons.Trash className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 태블릿 이상 기존 뷰 */}
          <div className="hidden md:block p-4">
            <Link to={`/inventory/${item.id}`} className="flex gap-5">
              {item.photos && item.photos[0] ? (
                <img
                  src={item.photos[0]}
                  alt={item.productName}
                  className="w-28 h-28 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                />
              ) : (
                <div className="w-28 h-28 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs flex-shrink-0">
                  이미지 없음
                </div>
              )}

              <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                <div>
                  <div className="flex justify-between items-start pr-10">
                    <h3 className="text-lg font-bold text-gray-900 mb-1 truncate group-hover:text-brand-600 transition-colors">{item.productName}</h3>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[item.status] || 'bg-gray-100 text-gray-800'}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2 line-clamp-1">{item.description || '설명 없음'}</p>
                </div>

                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block text-xs">카테고리</span>
                    <span className="font-medium text-gray-900">{item.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">위치</span>
                    <button
                      onClick={(e) => onLocationClick(item, e)}
                      className="font-medium text-brand-600 hover:text-brand-800 hover:underline flex items-center"
                    >
                      {getLocationDisplay(item)}
                      <Icons.Edit className="w-3 h-3 ml-1 opacity-50" />
                    </button>
                    {item.zoneName && (
                      <span className="text-xs text-purple-600 mt-0.5 block">📍 {item.zoneName.split(' - ').pop()}</span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">판매가(Vat excl.)</span>
                    <span className="font-bold text-brand-600">
                      {parseInt(item.salePrice || 0).toLocaleString()}원
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block text-xs">등록일</span>
                    <span className="text-gray-900">
                      {item.createdAt instanceof Date ? item.createdAt.toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
            <button
              onClick={(e) => onDelete(item, e)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              title="삭제"
            >
              <Icons.Delete className="w-5 h-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// 테이블 뷰 컴포넌트
function TableView({ items, onDelete, onLocationClick, getLocationDisplay }) {
  const statusColors = {
    '판매가능': 'bg-green-100 text-green-800',
    '수리중': 'bg-yellow-100 text-yellow-800',
    '청소중': 'bg-blue-100 text-blue-800',
    '예약': 'bg-purple-100 text-purple-800',
    '판매완료': 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-brand-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-[40px] px-1 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
              <th className="w-[20%] lg:w-[18%] px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">제품정보</th>
              <th className="hidden md:table-cell w-[12%] lg:w-[10%] px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">위치</th>
              <th className="hidden md:table-cell w-[15%] lg:w-[11%] px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">제품코드</th>
              <th className="hidden lg:table-cell w-[8%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">구매일</th>
              <th className="hidden lg:table-cell w-[8%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">입고일</th>
              <th className="hidden md:table-cell w-[8%] lg:w-[5%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">수량</th>
              <th className="hidden lg:table-cell w-[8%] px-2 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">구매가</th>
              <th className="hidden md:table-cell w-[12%] lg:w-[9%] px-2 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">판매가</th>
              <th className="hidden lg:table-cell w-[6%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">소유</th>
              <th className="hidden lg:table-cell w-[7%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">판매</th>
              <th className="w-[70px] md:w-[10%] lg:w-[7%] px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">상태</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {items.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                <td className="px-1 py-3 text-center">
                  <button
                    onClick={(e) => onDelete(item, e)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="삭제"
                  >
                    <Icons.Trash className="w-4 h-4" />
                  </button>
                </td>
                <td className="px-2 py-3 overflow-hidden">
                  <Link to={`/inventory/${item.id}`} className="flex items-center min-w-0">
                    <div className="h-9 w-9 flex-shrink-0">
                      {item.photos && item.photos[0] ? (
                        <img className="h-9 w-9 rounded object-cover" src={item.photos[0]} alt="" />
                      ) : (
                        <div className="h-9 w-9 rounded bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">No Img</div>
                      )}
                    </div>
                    <div className="ml-2 min-w-0 overflow-hidden">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-brand-600 truncate">{item.productName}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {item.categoryLarge || '-'}{item.categoryMedium && ` > ${item.categoryMedium}`}
                      </div>
                    </div>
                  </Link>
                </td>
                <td className="hidden md:table-cell px-2 py-3 overflow-hidden">
                  <button
                    onClick={(e) => onLocationClick(item, e)}
                    className="text-sm text-brand-600 hover:text-brand-800 hover:bg-brand-50 px-1 py-0.5 rounded transition-colors flex items-center truncate max-w-full"
                    title="위치 변경"
                  >
                    <span className="truncate">{getLocationDisplay(item)}</span>
                    <Icons.Edit className="w-3 h-3 ml-1 opacity-50 flex-shrink-0" />
                  </button>
                </td>
                <td className="hidden md:table-cell px-2 py-3 overflow-hidden">
                  <div className="text-sm text-gray-500 font-mono truncate">{item.barcodeId || '-'}</div>
                </td>
                <td className="hidden lg:table-cell px-2 py-3 text-sm text-center text-gray-600 truncate">
                  {item.purchaseDate || '-'}
                </td>
                <td className="hidden lg:table-cell px-2 py-3 text-sm text-center text-gray-600 truncate">
                  {item.arrivalBatch || '-'}
                </td>
                <td className="hidden md:table-cell px-2 py-3 text-sm text-center">
                  {(item.productType === 'bulk' || Number(item.quantity) > 1) ? (
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      Number(item.quantity || 1) === 0
                        ? 'bg-red-100 text-red-800'
                        : Number(item.quantity || 1) <= 5
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.quantity || 1}개
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">1개</span>
                  )}
                </td>
                <td className="hidden lg:table-cell px-2 py-3 text-sm text-right text-gray-600 truncate">
                  {parseInt(item.purchasePrice || 0).toLocaleString()}원
                </td>
                <td className="hidden md:table-cell px-2 py-3 text-sm text-right font-medium text-brand-600 truncate">
                  {parseInt(item.salePrice || 0).toLocaleString()}원
                </td>
                <td className="hidden lg:table-cell px-2 py-3 text-sm text-center text-gray-700 truncate">{item.ownershipType || '직판'}</td>
                <td className="hidden lg:table-cell px-2 py-3 text-sm text-center">
                  {(item.isSold === '판매완료' || (!item.isSold && item.status === '판매완료')) ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">완료</span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">판매중</span>
                  )}
                </td>
                <td className="px-2 py-3 text-center">
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${statusColors[item.status] || 'bg-gray-100 text-gray-800'}`}>
                    {item.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 재사용 가능한 그리드 카드 컴포넌트
function InventoryCard({ item, onDelete, onLocationClick, getLocationDisplay }) {
  const statusColors = {
    '판매가능': 'bg-green-100 text-green-800',
    '수리중': 'bg-yellow-100 text-yellow-800',
    '청소중': 'bg-blue-100 text-blue-800',
    '예약': 'bg-purple-100 text-purple-800',
    '판매완료': 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="group relative">
      <Link to={`/inventory/${item.id}`}>
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 hover:shadow-md transition-all duration-200 overflow-hidden h-full flex flex-col">
          <div className="aspect-w-16 aspect-h-12 relative overflow-hidden bg-gray-100 h-48">
            {item.photos && item.photos[0] ? (
              <img
                src={item.photos[0]}
                alt={item.productName}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                이미지 없음
              </div>
            )}
            <div className="absolute top-2 right-2">
              <span className={`inline-block px-2 py-1 rounded-md text-xs font-bold shadow-sm ${statusColors[item.status] || 'bg-white text-gray-800'}`}>
                {item.status}
              </span>
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-brand-600 transition-colors line-clamp-1">{item.productName}</h3>
            <p className="text-sm text-gray-500 mb-3 line-clamp-2 flex-1">{item.description || '설명 없음'}</p>

            <div className="space-y-2 text-sm pt-3 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">위치</span>
                <span className="font-medium text-gray-900">{getLocationDisplay(item)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">판매가(Vat excl.)</span>
                <span className="font-bold text-brand-600 text-base">
                  {parseInt(item.salePrice || 0).toLocaleString()}원
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
      <button
        onClick={(e) => onLocationClick(item, e)}
        className="absolute bottom-16 right-4 p-1.5 bg-white/90 text-brand-500 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 shadow-sm text-xs flex items-center"
        title="위치 변경"
      >
        <Icons.MapPin className="w-3 h-3 mr-1" />
        위치
      </button>
      <button
        onClick={(e) => onDelete(item, e)}
        className="absolute top-2 left-2 p-2 bg-white/90 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 shadow-sm"
        title="삭제"
      >
        <Icons.Trash className="w-4 h-4" />
      </button>
    </div>
  );
}

export default InventoryListEnhanced;
